/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import {
	BestPriceIcon,
	Button,
	ContentLoader,
	OrderSummaryBox,
	PageLead,
	Typography,
	Wrapper,
	withLocalization,
} from '@adac-mp/components';
import {
	IPrice,
	ITypeVignettesOrderManagementFields,
	OfferContext,
	VignetteContext,
	getCanceledOrderCollection,
	getLabelValue,
	getOrderUrlParams,
	getUrlBasePath,
	setRouteLangParam,
} from '@adac-mp/shared';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { useQuery } from '@apollo/client';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';

interface ICancelledOrder {
	productId?: string;
	orderNo: string;
	price?: IPrice;
	serviceCatalogName: string | undefined;
}

const CancelOrderConfirmationPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { orderInfo, lang, cancelledOrderInfo, labels } = useContext(OfferContext);
	const { updateContentfulLabels, contentfulLabels } = useContext(VignetteContext);
	const isDkv = orderInfo && 'serviceCatalogCode' in orderInfo && orderInfo.serviceCatalogCode.includes('DKV');
	const isFlexProduct = cancelledOrderInfo?.flexProduct;
	const [orderInformation, setOrderInformation] = useState<ICancelledOrder>();
	const [urlParams, setUrlParams] = useState<string | undefined>();
	const { viewport } = useViewport();

	const { error, data, loading } = useQuery(getCanceledOrderCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderManagementCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;

	useEffect(() => {
		if (cancelledOrderInfo) {
			setOrderInformation({
				productId: cancelledOrderInfo.orderDetails[0].supplierBookingNo,
				orderNo: cancelledOrderInfo.orderNo,
				price: cancelledOrderInfo.orderDetails[0].refundAmount,
				serviceCatalogName: cancelledOrderInfo.flexProduct
					? getLabelValue('mp.tollSectionFlex', labels) || 'Streckenmaut Flex'
					: cancelledOrderInfo.orderDetails[0].serviceCatalogName,
			});
		}
		if (orderInfo) {
			setOrderInformation({ orderNo: orderInfo.orderNo, price: orderInfo.price, serviceCatalogName: undefined });
		}
		const urlParam = getOrderUrlParams(location);
		setUrlParams(urlParam);

		if (!cancelledOrderInfo && !orderInfo) {
			if (isDkv) {
				window.location.href = 'https://www.adac.de/fahrzeugwelt/maut-vignette/mautbox';
			} else {
				const params = createSearchParams();
				params.set('orderHash', String(urlParam));
				navigate(getUrlBasePath(`/order-management${setRouteLangParam(lang)}`, params));
			}
		}
	}, []);

	const renderDkvCancellationDetails = () => (
		<div className="px-3 mb-5">
			<Typography className="text-center mb-5" component="p" size="lgEven">
				Bitte schicken Sie die Camper Mautbox an folgende Anschrift:{' '}
			</Typography>
			<Typography component="p" size="lgEven">
				c/o Arvato Supply Chain Solutions SE
			</Typography>
			<Typography component="p" size="lgEven">
				Toll4Europe
			</Typography>
			<Typography component="p" size="lgEven">
				W.-G.-Freund Str. 6
			</Typography>
			<Typography component="p" size="lgEven">
				06188 Landsberg
			</Typography>
			<Typography component="p" size="lgEven">
				Germany
			</Typography>
		</div>
	);

	const setPageHeading = () => {
		if (isDkv) return 'Ihre Stornierung war erfolgreich!';
		if (isFlexProduct) return currentLabels?.canceledFlexHeadline || 'Ihr Service wurde gelöscht';
		return currentLabels?.canceldOrderHeadline || 'Ihre Bestellung wurde storniert';
	};

	const setPageDescription = () => {
		if (isDkv)
			return 'Wir haben die Stornierung für Ihre Mautbox erhalten. In Kürze finden Sie in Ihrem Postfach eine Stornierungs-E-Mail mit allen wichtigen Informationen.';
		if (isFlexProduct)
			return (
				currentLabels?.flexCanceledText ||
				'Digitale Streckenmaut FLEX & Ihre Daten wurde erfolgreich gelöscht. Es werden keine automatischen Abbuchungen mehr durchgeführt.'
			);
		return (
			currentLabels?.orderCanceledText ||
			'Der Betrag Ihrer Stornierung wird innerhalb der nächsten fünf Werktage auf die von Ihnen gewählte Zahlungsmethode zurücküberwiesen.'
		);
	};

	const setProductLabel = () => {
		if (isDkv) return 'Stornierte Bestellnummer';
		if (isFlexProduct) return getLabelValue('mp.service', labels) || 'Service';
		return currentLabels?.canceledItem || 'Stornierter Artikel';
	};

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<Wrapper className="bg-white items-center min-h-[calc(100vh_-_56px)] sm:min-h-[calc(100vh_-_98px)]">
					<section className="w-full text-center md:self-center md:w-6/12">
						<PageLead
							icon={<BestPriceIcon />}
							headline={setPageHeading()}
							description={setPageDescription()}
						/>
						<OrderSummaryBox
							isCancelOrder
							hidePrice={isFlexProduct}
							variant={isDkv ? 'dkv' : 'vignette'}
							orderNumber={
								isDkv ? orderInformation?.orderNo || '' : String(orderInformation?.productId) || ''
							}
							serviceCatalogName={orderInformation?.serviceCatalogName}
							totalPrice={`${orderInformation?.price?.amount}€`}
							itemLabel={setProductLabel()}
							priceLabel={currentLabels?.canceledAmount || 'Erstatteter Betrag'}
						/>
						{isDkv && renderDkvCancellationDetails()}
						<div className="flex justify-center">
							{isDkv ? (
								<>
									<Button
										fullWidth
										href="https://www.adac.de/fahrzeugwelt/maut-vignette/mautbox"
										className="mb-5 md:max-w-[292px]">
										Zurück zur Startseite
									</Button>
									<Typography className="text-center px-3" component="p" size="lgEven">
										Für weitere Rückfragen kontaktieren Sie bitte unseren Kundenservice.
									</Typography>
									:
								</>
							) : (
								<Button
									fullWidth
									className={`mt-6 ${viewport === 'desktop' && 'flex-shrink max-w-[50%]'}`}
									onClick={() => {
										if (urlParams) {
											const params = createSearchParams();
											params.set('orderHash', String(urlParams));
											navigate(
												getUrlBasePath(`/order-management${setRouteLangParam(lang)}`, params),
											);
										}
									}}>
									{currentLabels?.backToOverview || 'Zurück zur Bestellübersicht'}
								</Button>
							)}
						</div>
					</section>
				</Wrapper>
			)}
		</>
	);
};

export const CancelOrderConfirmation = withLocalization(CancelOrderConfirmationPage);
